import React, { useCallback, useState } from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { ChildrenProps } from "../../../types";
import { DEFAULT_ACCRUAL_REVIEW_LEDGER_KEY } from "./accrualReview.constants";
import {
    AccrualReviewCtx,
    type AccrualLedgerDisplayVariant,
} from "./accrualReview.context";
import type { AccrualConfirmDisplayVariant } from "./accrualConfirmChangeCount";

interface AccrualReviewProviderProps extends ChildrenProps {
    confirmDisplay?: AccrualConfirmDisplayVariant;
    hasCellActionOpeners?: boolean;
    ledgerDisplay?: AccrualLedgerDisplayVariant;
}

export const AccrualReviewProvider: React.FC<AccrualReviewProviderProps> = ({
    children,
    confirmDisplay = "plain",
    hasCellActionOpeners = false,
    ledgerDisplay = "text",
}) => {
    const [overrides, setOverrides] = useState(
        () => new Map<number, Map<string, string>>(),
    );
    const [dismissedMatches, setDismissedMatches] = useState(
        () => new Set<number>(),
    );

    const setProposedAccountId = useCallback(
        (transactionId: number, accountId: string, ledgerName?: string) => {
            setOverrides((prev) => {
                const next = new Map(prev);
                const accountsByLedger = new Map(next.get(transactionId) ?? []);
                accountsByLedger.set(
                    ledgerName ?? DEFAULT_ACCRUAL_REVIEW_LEDGER_KEY,
                    accountId,
                );
                next.set(transactionId, accountsByLedger);
                return next;
            });
        },
        [],
    );

    const getProposedAccountId = useCallback(
        (transaction: TransactionListItem, ledgerName?: string) => {
            if (transaction.accrualAccountId && !ledgerName) {
                return transaction.accrualAccountId;
            }
            const accountsByLedger = overrides.get(transaction.id);
            return (
                accountsByLedger?.get(
                    ledgerName ?? DEFAULT_ACCRUAL_REVIEW_LEDGER_KEY,
                ) ??
                accountsByLedger?.get(DEFAULT_ACCRUAL_REVIEW_LEDGER_KEY) ??
                null
            );
        },
        [overrides],
    );

    const dismissMatch = useCallback((transactionId: number) => {
        setDismissedMatches((prev) => new Set(prev).add(transactionId));
        setOverrides((prev) => {
            const next = new Map(prev);
            next.delete(transactionId);
            return next;
        });
    }, []);

    const isMatchDismissed = useCallback(
        (transactionId: number) => dismissedMatches.has(transactionId),
        [dismissedMatches],
    );

    return (
        <AccrualReviewCtx.Provider
            value={{
                getProposedAccountId,
                setProposedAccountId,
                overrides,
                dismissMatch,
                isMatchDismissed,
                confirmDisplay,
                hasCellActionOpeners,
                ledgerDisplay,
            }}
        >
            {children}
        </AccrualReviewCtx.Provider>
    );
};
